import { authRedirectCheckRtkQuery } from '@autone/auth';
import { AgentApiPaths } from '@autone/openapi-typescript';
import { assertNever, createAppQueryBuilder } from '@autone/utils';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createApi } from '@reduxjs/toolkit/query/react';
import { v4 as uuid } from 'uuid';

import { customBaseQuery } from '../utils/custom-base-query';

import {
  type ChatMessage,
  type ChatRequestBody,
  chatStreamEventSchema,
} from './types';

const AGENT_API_PREFIX = '/agent';

const CHAT_PATH = `/v2${AgentApiPaths.chat_agent_conversations_chat_post}`;

const baseUrl = `https://${import.meta.env.VITE_BACKEND_ENDPOINT_HOST}`;

const buildHeaders = async (): Promise<Record<string, string>> => {
  const { idToken } = await authRedirectCheckRtkQuery();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  return headers;
};

class StreamError extends Error {}

export const agentQueryBuilder = createAppQueryBuilder<
  'agent',
  typeof AGENT_API_PREFIX
>();

export const agentApi = createApi({
  reducerPath: 'agentApi',
  baseQuery: async (args, api, extraOptions) => {
    return customBaseQuery(args, api, extraOptions, `v2${AGENT_API_PREFIX}`);
  },
  endpoints: (builder) => ({
    // Client-side cache holder for the conversation. The SSE stream from
    // sendChatMessage patches this entry via updateQueryData. When the BE
    // persists conversations this can become a real GET without consumer
    // changes.
    getChatMessages: builder.query<ChatMessage[], void>({
      queryFn: () => ({ data: [] }),
    }),
    sendChatMessage: builder.mutation<null, ChatRequestBody>({
      queryFn: async (body, { signal, dispatch }) => {
        const assistantId = uuid();
        const patchMessages = (
          recipe: (draft: ChatMessage[]) => ChatMessage[] | void,
        ) =>
          dispatch(
            agentApi.util.updateQueryData('getChatMessages', undefined, recipe),
          );

        patchMessages((draft) => {
          draft.push(
            { id: uuid(), role: 'user', content: body.prompt },
            { id: assistantId, role: 'assistant', content: '' },
          );
        });

        // fetchEventSource needs a controller we can abort ourselves on
        // [DONE], chained to RTK's signal so mutation.abort() also stops it.
        const controller = new AbortController();
        signal.addEventListener('abort', () => controller.abort());

        try {
          const headers = await buildHeaders();
          await fetchEventSource(`${baseUrl}${CHAT_PATH}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal: controller.signal,
            openWhenHidden: true,
            async onopen(response) {
              if (!response.ok) {
                throw new StreamError(`Request failed (${response.status})`);
              }
            },
            onmessage(ev) {
              if (ev.data === '[DONE]') {
                controller.abort();
                return;
              }
              let raw: unknown;
              try {
                raw = JSON.parse(ev.data);
              } catch {
                return;
              }
              const result = chatStreamEventSchema.safeParse(raw);
              if (!result.success) return;
              const parsed = result.data;
              switch (parsed.type) {
                case 'block_delta':
                  if (parsed.text) {
                    patchMessages((draft) => {
                      const message = draft.find((m) => m.id === assistantId);
                      if (message) message.content += parsed.text;
                    });
                  }
                  break;
                case 'error':
                  throw new StreamError(parsed.message);
                case 'block_start':
                case 'block_stop':
                  break;
                default:
                  assertNever(parsed);
              }
            },
            onerror(err) {
              throw err;
            },
          });
          return { data: null };
        } catch (err) {
          // A plain abort (cancel or [DONE]) keeps the partial message.
          if (controller.signal.aborted && !(err instanceof StreamError)) {
            return { data: null };
          }
          patchMessages((draft) =>
            draft.filter((message) => message.id !== assistantId),
          );
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: err instanceof Error ? err.message : 'Unknown error',
            },
          };
        }
      },
    }),
  }),
});

export const { useGetChatMessagesQuery, useSendChatMessageMutation } = agentApi;
